import {
    AuthContext,
    DataTable,
    DataTableCell,
    DataTablePresenter,
    IAuthContextProps,
    IFilterSchema,
    ITableColumn,
    ITableFetchOptions,
    ProviderChargemaster,
} from '@enter/core';
import { DataTableCard, PageHeader, PageLayout, PageLayoutRow } from 'components';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { useNavigate, useLocation, useMatch } from 'react-router-dom';
import { Row } from 'react-table';
import { DialogContext, IDialogContextProps } from 'services';
import { IInjectedRootStore, IObservedRootStore } from 'stores';
import { withRouter, RouterProps } from 'router/with-router';

interface IMatchParams {
    chargemasterItemId: string;
}
interface IChargemasterListingProps extends IInjectedRootStore, RouterProps<IMatchParams> {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
}

interface IChargemasterListingState {
    loading: boolean;
    options: ITableFetchOptions | undefined;
}

const columns: ITableColumn<ProviderChargemaster>[] = [
    {
        id: 'name',
        Header: 'Name',
        accessor: 'name',
        disableSortBy: true,
    },
    {
        id: 'description',
        Header: 'Description',
        accessor: 'description',
        disableSortBy: true,
    },
    {
        id: 'fromDos',
        Header: 'From DOS',
        accessor: 'fromDos',
        Cell: DataTableCell('Date'),
        disableSortBy: true,
    },
    {
        id: 'throughDos',
        Header: 'Through DOS',
        accessor: 'throughDos',
        Cell: DataTableCell('Date'),
        disableSortBy: true,
    },
];
const filterSchemas: IFilterSchema<string>[] = [
    {
        label: 'Name',
        filterId: 'filterName',
        columnId: 'name',
        filterType: 'String',
    },
];

class ChargemasterListPage extends React.Component<IChargemasterListingProps, IChargemasterListingState> {
    private presenter: DataTablePresenter<ProviderChargemaster>;

    constructor(props: IChargemasterListingProps) {
        super(props);
        this.state = {
            loading: false,
            options: undefined,
        };
        this.presenter = new DataTablePresenter<ProviderChargemaster>({
            name: 'chargemasterListing',
            data: [],
            columns,
            filterSchemas,
        });
    }

    public componentDidMount(): void {
        document.title = 'ENTER Platform - Chargemasters';
    }
    public componentWillUnmount(): void {
        this.authContext.cancelGroup('chargemasters');
    }

    public render(): React.ReactElement {
        return (
            <PageLayout>
                <PageHeader title="Chargemasters" hideDateRange={true} />
                <PageLayoutRow expand={true}>
                    <DataTableCard size="large">
                        <DataTable
                            presenter={this.presenter}
                            isLoading={this.state.loading}
                            onFetch={this.onFetch}
                            onRowClick={this.onRowClick}
                            showExport={true}
                        />
                    </DataTableCard>
                </PageLayoutRow>
            </PageLayout>
        );
    }

    private get authContext(): IAuthContextProps {
        return this.props.authContext;
    }

    private get dialogContext(): IDialogContextProps {
        return this.props.dialogContext;
    }

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        if (!_.isEqual(this.state.options, fetchOptions)) {
            try {
                this.setState({
                    options: fetchOptions,
                    loading: true,
                });
                this.authContext.cancelGroup('chargemasters');
                this.authContext.setGroup('chargemasters');

                const { pageIndex, pageSize, validFilters } = fetchOptions;
                const chargemasterListing = await this.props.rootStore.chargemasterStore.loadChargemasterListing({
                    page: pageIndex,
                    pageSize,
                    filters: validFilters,
                });
                this.presenter.onDataUpdate(chargemasterListing.chargemasters || []);
                this.presenter.onDataTotalChange(chargemasterListing.total || 0);
                this.setState({
                    loading: false,
                });
            } catch (err) {
                this.dialogContext.showError(err);
            }
        }
    };

    private onRowClick = (row: Row<ProviderChargemaster>): void => {
        // Extract accountId from URL
        const getAccountId = (): string | undefined => {
            const pathParts = window.location.pathname.split('/').filter(Boolean);
            return pathParts.length > 0 && pathParts[0].length === 36 ? pathParts[0] : undefined;
        };
        const accountId = getAccountId();
        const path = accountId ? `/${accountId}/chargemasters/${row.original.id}` : `/chargemasters/${row.original.id}`;
        this.props.history.push(path);
    };
}

const ChargemasterListPageWithRouter = withRouter(ChargemasterListPage);

export const ChargemasterListing: React.FC<IObservedRootStore> = inject('rootStore')(
    observer(
        (props: IObservedRootStore): React.ReactElement => {
            const { rootStore } = props;
            if (!rootStore) {
                return <></>;
            }
            const authContext = useContext(AuthContext);
            const dialogContext = useContext(DialogContext);
            return (
                <ChargemasterListPageWithRouter
                    authContext={authContext}
                    dialogContext={dialogContext}
                    rootStore={rootStore}
                />
            );
        },
    ),
);
